/**
 * B.11 Game Report — types, live-calc and POST payload builder.
 *
 * Live-calc (screen-11 spec):
 *   Итого(расчёт) = quest + tea + mc + night + Σуслуги − Σскидки
 *   Факт          = Σ(6 платёжных полей) − Сдача
 *   Разница       = Факт − Итого   (|·|<1 → сходится)
 * Скидка: pct → round(base·pct/100) где base = quest+tea+mc+night+услуги; fix → сумма.
 *
 * Backend (POST /api/game-reports) хранит 6 платёжных колонок: cash, terminal,
 * transfer, prepaid_used, invoice, other («Друг.»). Колонка other заводится
 * само-миграцией в POST (idempotent ALTER) и входит в total/received recalcs.
 */

export type ReportMode = "new" | "fix" | "edit" | "partner";

export interface ModalGame {
  bookingId: number;
  questName: string;
  shortName: string;
  time: string;
  date: string; // YYYY-MM-DD
  clientName: string | null;
  bookedPlayers: number;
  source: string;
  tea: boolean;
  teaRoomId: string | null;
  mc: string | null;
  branchId: string | null;
  operatorId: string;
  operatorName: string;
  mode: ReportMode;
  correctionReason?: string;
  reportId?: number;
  prefill?: ReportPrefill;
}

export interface ReportPrefill {
  players: number;
  cash: number;
  terminal: number;
  transfer: number;
  prepaidUsed: number;
  invoice: number;
  activity: string;
  comment: string;
}

export interface CalcDefaults {
  questPrice: number;
  teaPrice: number;
  mcPrice: number;
  nightSurcharge: number;
  prepaid: number;
}

export interface ServiceLine {
  id: string;
  name: string;
  amount: number;
}

export type DiscountType = "pct" | "fix";

export interface DiscountLine {
  id: string;
  type: DiscountType;
  value: number;
  reason: string;
}

export interface PayState {
  cash: number;
  terminal: number;
  transfer: number;
  prepaidUsed: number; // ЗДТК
  invoice: number; // Р/С
  other: number; // Друг. (folded into comment)
}

export const SERVICE_OPTIONS = [
  "Дополнительный квест",
  "Подарочный сертификат",
  "Аренда реквизита",
  "Сладкий стол",
  "Доп. время",
] as const;

export const DISCOUNT_REASONS = [
  "День рождения",
  "Постоянный клиент",
  "Акция",
  "Промокод",
  "Соц. скидка",
  "Корпоратив",
] as const;

export const CHANGE_SOURCES = [
  { id: "safe", label: "Сейф админа" },
  { id: "self", label: "Я сам" },
  { id: "oo", label: "Операционный офис" },
  { id: "operator", label: "От сотрудника" },
] as const;

let seq = 0;
export function lineId(prefix: string): string {
  seq += 1;
  return `${prefix}-${seq}`;
}

/** Σ скидок в рублях для текущей базы. */
export function discountAmount(d: DiscountLine, base: number): number {
  if (d.type === "pct") return Math.round((base * (d.value || 0)) / 100);
  return Math.max(0, Math.round(d.value || 0));
}

export interface CalcResult {
  baseCalc: number; // quest+tea+mc+night
  servicesSum: number;
  discountsSum: number;
  calcTotal: number; // baseCalc + services − discounts
  fact: number; // Σpay − change
  diff: number; // fact − calcTotal
  reconciles: boolean;
}

export function computeCalc(args: {
  questPrice: number;
  teaPrice: number;
  mcPrice: number;
  nightSurcharge: number;
  services: ServiceLine[];
  discounts: DiscountLine[];
  pay: PayState;
  change: number;
}): CalcResult {
  const baseCalc = args.questPrice + args.teaPrice + args.mcPrice + args.nightSurcharge;
  const servicesSum = args.services.reduce((s, x) => s + (x.amount || 0), 0);
  const discBase = baseCalc + servicesSum;
  const discountsSum = args.discounts.reduce((s, d) => s + discountAmount(d, discBase), 0);
  const calcTotal = Math.max(0, baseCalc + servicesSum - discountsSum);
  const fact =
    args.pay.cash + args.pay.terminal + args.pay.transfer + args.pay.prepaidUsed + args.pay.invoice + args.pay.other - args.change;
  const diff = fact - calcTotal;
  return {
    baseCalc,
    servicesSum,
    discountsSum,
    calcTotal,
    fact,
    diff,
    reconciles: Math.abs(diff) < 1,
  };
}

/** "₽6 400" — thousands with thin space. */
export function rub(n: number): string {
  const neg = n < 0;
  const s = Math.abs(Math.round(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${neg ? "−" : ""}₽${s}`;
}

export interface ReportPayload {
  booking_id: number;
  operator_id: string;
  date: string;
  actual_players: number;
  cash: number;
  terminal: number;
  transfer: number;
  prepaid_used: number;
  invoice: number;
  other: number;
  total: number;
  expected_total: number;
  quest_price: number;
  tea_price: number;
  mc_price: number;
  night_surcharge: number;
  comment: string;
  activity: string;
  change_amount: number;
  change_source: string;
  change_source_id: string;
  tea_cleanup: number;
  tea_room_id: string | null;
  taxi_amount: number; mk_status: string; is_primary: number;
  achievements?: string[];
}

export function buildPayload(args: {
  game: ModalGame;
  players: number;
  questPrice: number;
  teaPrice: number;
  mcPrice: number;
  nightSurcharge: number;
  services: ServiceLine[];
  discounts: DiscountLine[];
  pay: PayState;
  change: number;
  changeSource: string;
  changeSourceId: string;
  teaRoomId: string | null;
  teaCleanup: boolean;
  taxi: number;
  mkStatus: string;
  activity: string; comment: string; isPrimary: boolean;
  achievements?: string[];
  calc: CalcResult;
}): ReportPayload {
  const extras = [
    ...args.services.map((s) => `${s.name}: ${rub(s.amount)}`),
    ...args.discounts.map((d) => `скидка ${d.type === "pct" ? `${d.value}%` : rub(d.value)} (${d.reason})`),
  ];
  let comment = args.comment.trim();
  if (extras.length) comment = [comment, extras.join("; ")].filter(Boolean).join(" · ");

  return {
    booking_id: args.game.bookingId,
    operator_id: args.game.operatorId,
    date: args.game.date,
    actual_players: args.players,
    cash: args.pay.cash,
    terminal: args.pay.terminal,
    transfer: args.pay.transfer,
    prepaid_used: args.pay.prepaidUsed,
    invoice: args.pay.invoice,
    other: args.pay.other,
    total: args.calc.fact,
    expected_total: args.calc.calcTotal,
    quest_price: args.questPrice,
    tea_price: args.teaPrice,
    mc_price: args.mcPrice,
    night_surcharge: args.nightSurcharge,
    comment,
    activity: args.activity.trim(),
    change_amount: args.change,
    change_source: args.changeSource,
    change_source_id: args.changeSourceId,
    tea_cleanup: args.teaCleanup ? 1 : 0,
    tea_room_id: args.teaRoomId,
    taxi_amount: args.taxi,
    mk_status: args.mkStatus,
    is_primary: args.isPrimary ? 1 : 0,
    achievements: args.achievements,
  };
}
